/**
 * ROLE CHECKS — the ONLY correct way to test what a member is.
 *
 * Never write `user.role === 'student'`. That field is the single denormalised
 * PRIMARY role; a member commonly holds several. Reading it directly is a bug, and
 * it is a quiet one: the member simply vanishes from a list, or a control they
 * should have never appears, and nothing errors.
 *
 * It has cost us real money and real trust already. Shady Tarek — student+coach,
 * 4,570 XP — was absent from the Mark Attendance roster for months because of it, so
 * he could not be marked present, earned no XP, and consumed no class credit against
 * his subscription.
 *
 * Use isActiveStudent / isCoach / isParent / isCoordinator / isAdmin / isKidMember,
 * or hasAnyRole. They resolve the union of `role`, `roleNames`, `roleIds` (through
 * the roles map) and `roleId`, and compare names with separators normalised.
 */
import type { User } from '@/types';
import { getRoleConfig } from '@/lib/roleConfig';
import { getRoleNameMap } from '@/lib/roleNameMap';

/**
 * Does the user hold a role with this NAME, under any of their roles?
 *
 * Capability must be the UNION of every role a user holds — Moaaz is
 * student+coordinator, Shady is coach+coordinator. `user.role` is only the single
 * denormalized PRIMARY role, and `roleIds` holds Firestore role-DOC IDs (e.g.
 * 'QAmMRbVe7MJSqg4uxMvF'), not names — so matching names against roleIds silently
 * fails for real multi-role users. `roleNames` is the resolved-name array
 * AuthProvider populates from the role docs, and is the reliable source.
 * roleIds/roleId are still checked last for legacy rows that stored names there.
 */
/**
 * Role names are compared with separators normalised: the role DOCUMENT is called
 * "Super Admin" (→ 'super admin') while the code asks for 'super_admin'. Without
 * this, admin granted through the Roles UI alone is invisible to isAdmin() — a gap
 * pinned in the rules suite and, until now, live in the client too.
 */
const norm = (s: string): string => s.toLowerCase().trim().replace(/[\s_-]+/g, '_');

function hasRoleNamed(user: User | null | undefined, name: string): boolean {
    if (!user) return false;
    const wanted = norm(name);

    if (user.role && norm(user.role) === wanted) return true;

    if (Array.isArray(user.roleNames)
        && user.roleNames.some(r => typeof r === 'string' && norm(r) === wanted)) return true;

    // Legacy: some rows stored role NAMES in roleIds / roleId.
    if (Array.isArray(user.roleIds)
        && user.roleIds.some(r => typeof r === 'string' && norm(r) === wanted)) return true;

    // roleIds normally holds Firestore DOC IDS, so the name comparison above never
    // matches for real multi-role users. Resolve them through the roles map — without
    // this, `roleNames` is the only path that understands roleIds, and roleNames is
    // never persisted (AuthProvider derives it for the SIGNED-IN user only). That is
    // why a coach-who-is-also-a-student disappeared from every student roster.
    if (Array.isArray(user.roleIds)) {
        const map = getRoleNameMap();
        if (user.roleIds.some(r => typeof r === 'string' && map[r] && norm(map[r]) === wanted)) return true;
    }

    const mappedRoleId = typeof user.roleId === 'string' ? getRoleNameMap()[user.roleId] : undefined;
    if (mappedRoleId && norm(mappedRoleId) === wanted) return true;

    if (typeof user.roleId === 'string' && norm(user.roleId) === wanted) return true;

    return false;
}

/**
 * Checks if a user has student capabilities/role.
 * Supports legacy 'role' string and new multi-role 'isStudent'/'roleIds' patterns.
 */
export const isActiveStudent = (user: User | null | undefined): boolean => {
    if (!user) return false;

    // Emergency whitelist for staff who also participate as students
    const STUDENT_WHITELIST = [
        'wzuZOIiMz4OYYoePWkit7O8MzYv2',
        'IT0Ym31B8UMXdBFuOf3A5lNfQ9m2',
        'uujGwUzpxUXG5j7VJiAT1NAHmtW2' // Parent+Student dual role
    ];
    if (STUDENT_WHITELIST.includes(user.uid)) return true;

    // Explicit boolean flag (multi-role support)
    if (user.isStudent === true) return true;

    // Any held role named student/kid (covers secondary roles, not just the primary)
    return hasRoleNamed(user, 'student') || hasRoleNamed(user, 'kid');
};

/**
 * Checks if a user has coach capabilities/role.
 */
export const isCoach = (user: User | null | undefined): boolean => hasRoleNamed(user, 'coach');

/**
 * Checks if a user is a coordinator.
 * Supports:
 * - Primary role field: user.role === 'coordinator'
 * - roleNames array: Contains resolved role names (populated by AuthProvider)
 * - roleIds array: Fallback check for string 'coordinator' 
 * - roleId field: Fallback check for string 'coordinator'
 */
export const isCoordinator = (user: User | null | undefined): boolean => {
    if (!user) return false;

    // Emergency whitelist: super-admin UIDs from config/roleConfig.json
    // (Task #15 — was a hard-coded `'162p0Oib...'` literal).
    if (getRoleConfig().superAdminUids.includes(user.uid)) return true;

    return hasRoleNamed(user, 'coordinator');
};

/**
 * Super admin ONLY — deliberately narrower than isAdmin(), which also matches plain
 * 'admin'. Use this for controls that must stay with the academy owner: full
 * impersonation, points and quota adjustment.
 *
 * Exists because those were gated on a raw `user.role === 'super_admin'`, which
 * cannot see a super-admin granted through the Roles UI (roleIds holds doc ids). It
 * happened to be correct — Ahmed is the only super admin and carries the legacy
 * field — but "correct by luck" is not a state to leave the most powerful control in
 * the product in. Verified before shipping: nobody newly gains impersonation.
 */
export const isSuperAdmin = (user: User | null | undefined): boolean =>
    hasRoleNamed(user, 'super_admin');

/**
 * Checks if a user is an admin or super_admin.
 */
export const isAdmin = (user: User | null | undefined): boolean =>
    hasRoleNamed(user, 'super_admin') || hasRoleNamed(user, 'admin');

/**
 * THE division rule: is this member a kid? `role === 'kid'`, and nothing else.
 *
 * Decided 17 Sept 2026 from live data: `role === 'kid'` and a parent uid in `parentId`
 * identify the same 31 of 158 members. Never test `parentId` for PRESENCE: 21 adults
 * (students, parents, coaches) carry `parentId: null`, written when an admin saves a
 * non-kid in the member editor. Every reader treats it as a link (truthy / == uid), so
 * null is harmless there — but `'parentId' in user` counts 52 "kids". Every other signal is
 * weaker — `isKid` is set on only 8 of the 31 (half-migrated, now retired and no
 * longer written), `isKidProfile` means "managed by a parent without credentials",
 * which is a different question, and age/belt are never used: a seven-year-old
 * white belt is a kid, a thirty-year-old white belt is not, and belt colour cannot
 * tell them apart.
 *
 * Seven different kid tests existed before this (isKid || isKidProfile || role,
 * plus age < 18 on the leaderboard and age < 16 in finance). They agreed on all 158
 * members on the day they were collapsed — this changed no one's division — but
 * they would have drifted apart the first time one field was edited without the
 * others. Mirrored server-side in functions/src/division.ts; keep them identical.
 *
 * When `roleIds` is backfilled from `role` and legacy `role` reads are retired,
 * this is the one place that has to change.
 */
export const isKidMember = (user: { role?: string | null } | null | undefined): boolean =>
    user?.role === 'kid';

/**
 * Checks if a user has parent capabilities/role.
 */
export const isParent = (user: User | null | undefined): boolean => hasRoleNamed(user, 'parent');

/**
 * Checks if a user has any of the specified roles.
 * Supports both legacy role and roleIds array.
 */
export const hasAnyRole = (user: User | null | undefined, roles: string[]): boolean =>
    roles.some(r => hasRoleNamed(user, r));
